// Utility functions for handling images

/**
 * Downloads an image from a URL as a file
 * @param imageUrl URL of the image to download
 * @param filename Filename to use for the downloaded image
 */
export const downloadImage = async (imageUrl: string, filename: string = 'map-image.jpg'): Promise<void> => {
  try {
    console.log('Iniciando descarga de imagen desde URL:', imageUrl.substring(0, 50) + '...');
    
    // Fetch the image data
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    console.log('Imagen recuperada exitosamente, tamaño:', Math.round(blob.size / 1024), 'KB');
    
    // Create a download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    console.log('Descarga completada');
  } catch (error) {
    console.error('Error downloading image:', error);
    throw error;
  }
};

/**
 * Fetches an image and returns it as a data URL
 * @param imageUrl URL of the image to fetch
 * @returns The image as a data URL
 */
export const fetchImageAsDataUrl = async (imageUrl: string): Promise<string | null> => {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image as data URL:', error);
    return null;
  }
};

/**
 * Convert a data URL to a Blob object
 * @param dataUrl The data URL to convert
 * @returns A Blob object representing the data
 */
export const dataURLToBlob = (dataUrl: string): Blob => {
  // Validate the data URL format
  if (!dataUrl || typeof dataUrl !== 'string') {
    console.error('Invalid data URL: not a string or empty');
    return createFallbackBlob();
  }
  
  if (!dataUrl.startsWith('data:')) {
    console.error('Invalid data URL format: does not start with "data:"', dataUrl.substring(0, 30));
    return createFallbackBlob();
  }

  try {
    console.log('Convirtiendo dataURL a blob, longitud de URL:', dataUrl.length);
    
    // Handle invalid input gracefully
    const arr = dataUrl.split(',');
    if (arr.length < 2) {
      console.error('Invalid data URL format: missing comma separator');
      return createFallbackBlob();
    }
    
    // Get MIME type from the data URL
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    
    // Check for base64 encoding
    const isBase64 = arr[0].includes(';base64');
    
    // The content should be after the comma
    const content = arr[1];
    if (!content || content.trim() === '') {
      console.error('Invalid data URL: empty content after comma');
      return createFallbackBlob();
    }
    
    if (isBase64) {
      try {
        // Convert base64 to binary
        const binaryString = atob(content);
        console.log('Base64 decodificado, longitud:', binaryString.length);
        
        const bytes = new Uint8Array(binaryString.length);
        
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const blob = new Blob([bytes], { type: mime });
        console.log('Blob creado correctamente, tamaño:', Math.round(blob.size / 1024), 'KB');
        return blob;
      } catch (atobError) {
        console.error('Error decoding base64 content:', atobError);
        return createFallbackBlob();
      }
    } else {
      // Handle non-base64 data URLs (URL-encoded)
      try {
        const decodedContent = decodeURIComponent(content);
        return new Blob([decodedContent], { type: mime });
      } catch (decodeError) {
        console.error('Error decoding URL-encoded content:', decodeError);
        return createFallbackBlob();
      }
    }
  } catch (error) {
    console.error('Error converting data URL to blob:', error);
    return createFallbackBlob();
  }
};

/**
 * Creates a small transparent fallback image blob
 * @returns A Blob with a transparent 1x1 GIF
 */
const createFallbackBlob = (): Blob => {
  // This is a 1x1 transparent GIF
  console.log('Creando imagen GIF de respaldo');
  return new Blob([
    new Uint8Array([
      0x47, 0x49, 0x46, 0x38, 0x39, 0x61, 0x01, 0x00, 0x01, 0x00, 0x80, 0x00, 0x00, 0xFF, 0xFF, 0xFF, 
      0x00, 0x00, 0x00, 0x21, 0xF9, 0x04, 0x01, 0x00, 0x00, 0x00, 0x00, 0x2C, 0x00, 0x00, 0x00, 0x00, 
      0x01, 0x00, 0x01, 0x00, 0x00, 0x02, 0x02, 0x44, 0x01, 0x00, 0x3B
    ])
  ], { type: 'image/gif' });
};

/**
 * Downloads a blob as a file
 * @param blob The blob to download
 * @param filename The filename to use
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  try {
    if (!blob || blob.size === 0) {
      console.error('Error: Empty or invalid blob provided for download');
      throw new Error('Invalid blob for download');
    }
    
    console.log('Iniciando descarga de blob, tamaño:', Math.round(blob.size / 1024), 'KB');
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    
    console.log('Descarga de blob completada');
  } catch (error) {
    console.error('Error downloading blob:', error);
    throw error;
  }
};

/**
 * Creates a simple image blob with text that can be downloaded
 * when no real image is available
 */
export const createTextImageBlob = async (text: string): Promise<Blob> => {
  console.log('Creando imagen de texto de respaldo:', text);
  
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 300;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('No se pudo obtener el contexto 2D del canvas');
    return createFallbackBlob();
  }
  
  // Fill background
  ctx.fillStyle = '#f0f0f0';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Add border
  ctx.strokeStyle = '#ccc';
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
  
  // Add text
  ctx.fillStyle = '#333';
  ctx.font = 'bold 24px Arial';
  
  // Center text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  
  // Add subtle descriptive text below
  ctx.font = '14px Arial';
  ctx.fillStyle = '#666';
  ctx.fillText('La imagen del mapa no está disponible', canvas.width / 2, canvas.height / 2 + 40);
  
  // Convert canvas to blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        console.log('Imagen de texto creada correctamente, tamaño:', Math.round(blob.size / 1024), 'KB');
      } else {
        console.error('Error al convertir canvas a blob');
      }
      resolve(blob || createFallbackBlob());
    }, 'image/jpeg', 0.9);
  });
};